// Error states — design handoff "magig Error States" §5.
// Four variants total; this file ships the two we actually need today:
//   - <BoundaryError /> wired as the router's defaultErrorComponent (route
//     loader throws, render crashes, etc.)
//   - <NotFoundError /> wired as the router's defaultNotFoundComponent
// Network + inline variants land when there's something to crash inline.

import { Trans } from '@lingui/react/macro';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { BrandSparkle, Mag } from './mag.js';

// ─── Shared primitives ─────────────────────────────────────────────────────

function ErrorWordmark() {
  return (
    <Link
      to="/"
      className="font-display text-[22px] font-black tracking-[-0.045em] text-[var(--ink)]"
    >
      magig
    </Link>
  );
}

function ErrorEyebrow({
  children,
  tone = 'plum',
}: {
  children: React.ReactNode;
  tone?: 'plum' | 'pulse' | 'quiet';
}) {
  const color =
    tone === 'pulse'
      ? 'text-[var(--pulse)]'
      : tone === 'quiet'
        ? 'text-[var(--ink)]/55'
        : 'text-[var(--plum)]';
  return (
    <p className={`font-mono text-[10px] font-semibold uppercase tracking-[0.12em] ${color}`}>
      {children}
    </p>
  );
}

// ─── 01 · BOUNDARY ─────────────────────────────────────────────────────────
// Renders for any route-loader error or render crash caught by TanStack
// Router. Two visual variants share the entry point:
//   - JS crash (the dashboard literally bugged out) → the dramatic
//     "wasn't supposed to happen" treatment with Mag napping + a stack
//     trace panel. This is the "report this to mag" path.
//   - Network / API failure (the dashboard is fine, the api isn't
//     reachable or returned an error) → a softer "can't reach the
//     server" variant with no stack trace and no "we reported this"
//     line. A flaky wifi shouldn't make the founder think mag crashed.

export interface BoundaryErrorProps {
  error: Error;
  reset?: (() => void) | undefined;
}

/** True when the error is a network blip or api-shaped HTTP failure —
 *  i.e. not a dashboard JS bug. Returning true short-circuits the
 *  full "we crashed" treatment in favor of the connection-issue
 *  variant.
 *
 *  Three families covered:
 *  - Browser fetch failures: TypeError with "failed to fetch" /
 *    "network error" / "load failed" (Safari) in the message.
 *  - Aborted requests: AbortError + AbortSignal-related throws.
 *  - Magig api errors: thrown from apps/dashboard/src/lib/api.ts with
 *    messages shaped `${METHOD} /api/<path> <status>` — they bubble
 *    through ensureQueryData when the route loader can't get data.
 *
 *  Keep this conservative: anything that's NOT clearly a network /
 *  endpoint failure falls through to the dramatic boundary so real
 *  bugs still get the loud surface they deserve. */
function isNetworkOrApiError(error: Error): boolean {
  if (error.name === 'AbortError') return true;
  const msg = error.message ?? '';
  if (error.name === 'TypeError' && /failed to fetch|network\s?error|load failed/i.test(msg)) {
    return true;
  }
  // api.ts throw pattern: "GET /api/brands 500 ..." / "PUT /api/foo 503"
  if (/^(GET|POST|PUT|DELETE|PATCH)\s+\/api\/.+\s+\d{3}/.test(msg)) {
    return true;
  }
  return false;
}

function generateIncidentId(error: Error): string {
  // Deterministic-ish from the error message + a session-stable timestamp
  // bucket so the same crash shows the same ID across re-renders. Not for
  // security — just for "tell mag inc_abcd123".
  const seed = `${error.name}:${error.message}:${Math.floor(Date.now() / 60000)}`;
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash << 5) - hash + seed.charCodeAt(i);
    hash |= 0;
  }
  return `inc_${(hash >>> 0).toString(16).slice(0, 7)}`;
}

function formatUtc(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(
    date.getUTCDate(),
  )} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())} UTC`;
}

/** Dispatcher — picks between the dramatic JS-crash surface and the
 *  softer network/api variant. Each branch is its own component so
 *  hook ordering stays consistent across renders. */
export function BoundaryError({ error, reset }: BoundaryErrorProps) {
  if (isNetworkOrApiError(error)) {
    return <NetworkError error={error} reset={reset} />;
  }
  return <JsCrashError error={error} reset={reset} />;
}

function JsCrashError({ error }: BoundaryErrorProps) {
  const [copied, setCopied] = useState(false);
  const incidentId = generateIncidentId(error);
  const timestamp = formatUtc(new Date());
  const stackLines = (error.stack ?? '')
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0 && !line.startsWith(error.name));

  const handleCopy = async () => {
    const payload = [
      `${error.name}: ${error.message}`,
      ...stackLines,
      '',
      `incident: ${incidentId} · ${timestamp}`,
    ].join('\n');
    try {
      await navigator.clipboard.writeText(payload);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard refused (e.g. http context) — silent. The user can still
      // screenshot the panel.
    }
  };

  const handleRefresh = () => {
    // Hard reload, always. reset() re-runs the throwing route inside
    // the same SPA process, which usually re-throws the exact same
    // error (the bad cache / bad state hasn't gone anywhere). A full
    // navigation drops every cache + every in-flight request, which
    // is what "refresh" means to a founder. Cheap to land on at the
    // current scale; revisit if we get a "lost work on refresh"
    // complaint.
    window.location.reload();
  };

  return (
    <main className="relative flex min-h-screen flex-col overflow-hidden bg-[var(--cream)] text-[var(--ink)]">
      {/* Corner glyph watermark */}
      <BrandSparkle
        size={320}
        color="var(--ink)"
        style={{
          position: 'absolute',
          top: -50,
          right: -50,
          opacity: 0.08,
          pointerEvents: 'none',
        }}
      />

      <header className="relative flex items-center justify-between px-8 py-5">
        <ErrorWordmark />
        <ErrorEyebrow tone="quiet">
          {'↘'} <Trans>error · boundary</Trans>
        </ErrorEyebrow>
      </header>

      <div className="relative flex flex-1 items-center justify-center px-8 pb-10">
        <div className="flex w-full max-w-[580px] flex-col items-start gap-5">
          <Mag size={140} mood="napping" />

          <div>
            <ErrorEyebrow>
              {'↘'} <Trans>mag is out cold</Trans>
            </ErrorEyebrow>
            <h1 className="mt-2.5 font-display text-[64px] font-black leading-[0.92] tracking-[-0.05em]">
              <Trans>
                well, that
                <br />
                <span className="font-serif font-normal text-[var(--plum)]">
                  wasn't supposed to happen.
                </span>
              </Trans>
            </h1>
          </div>

          <p className="max-w-[520px] font-serif text-[22px] leading-[1.35] text-[var(--ink)]/85">
            <Trans>
              “the dashboard crashed on me. nothing's lost — your campaigns are still running. just
              refresh and i'll be back.”
            </Trans>
          </p>

          <div className="flex flex-wrap gap-2.5">
            <button type="button" onClick={handleRefresh} className="btn">
              <Trans>refresh the page</Trans>
              <span className="btn-arrow">→</span>
            </button>
            {/* /login is the always-safe fallback — / and /home both pass
                through guards that may be the source of the crash. */}
            <Link to="/login" className="btn btn-ghost">
              <Trans>back to log in</Trans>
            </Link>
          </div>

          <div className="mt-2 w-full rounded-xl border-[1.5px] border-[var(--line-strong)] bg-white p-4">
            <div className="mb-2.5 flex items-center justify-between">
              <ErrorEyebrow>
                {'↘'} <Trans>error details · for the nerds</Trans>
              </ErrorEyebrow>
              <button
                type="button"
                onClick={handleCopy}
                className="cursor-pointer font-mono text-[10px] text-[var(--ink)]/55 hover:text-[var(--ink)]"
              >
                {copied ? <Trans>copied ✓</Trans> : <Trans>copy ⌘C</Trans>}
              </button>
            </div>
            <div className="font-mono text-[11px] leading-[1.65] text-[var(--ink)]/85">
              <div>
                <span className="font-bold text-[var(--pulse)]">{error.name}</span>
                {error.message ? <>: {error.message}</> : null}
              </div>
              {stackLines.slice(0, 4).map((line, i) => (
                <div key={line} className="mt-1.5" style={{ opacity: i < 2 ? 0.7 : 0.5 }}>
                  {line}
                </div>
              ))}
              <div className="mt-2.5 text-[var(--ink)]/50">
                <Trans>incident:</Trans>{' '}
                <span className="font-semibold text-[var(--ink)]">
                  {incidentId} · {timestamp}
                </span>
              </div>
            </div>
          </div>

          <p className="font-mono text-[11px] leading-[1.5] text-[var(--ink)]/55">
            {'↘'} <Trans>this has been reported automatically. mag will get it fixed.</Trans>
          </p>
        </div>
      </div>
    </main>
  );
}

// ─── 02 · NETWORK / API FAILURE ────────────────────────────────────────────
// Softer sibling of BoundaryError. Fires when the api is unreachable or
// returned an HTTP error during a route loader's data fetch. No stack
// trace, no incident id, no "we've reported this" — those are bug-
// reporting affordances, and an api blip isn't a bug. Keeps the same
// chrome (cream bg, sparkle watermark, wordmark + eyebrow header) so
// the visual family reads as "still magig", just framed honestly.

function NetworkError({ error, reset }: BoundaryErrorProps) {
  const handleTryAgain = () => {
    // reset() re-runs the throwing loader inside the same SPA process —
    // exactly what we want for a transient network blip. The api is
    // probably back; the cache is empty for the failing query; one more
    // pass should land. Falls back to a hard reload if the router
    // didn't supply a reset (defensive — TanStack Router always does).
    if (reset) {
      reset();
      return;
    }
    window.location.reload();
  };

  // Parse the api-error shape "GET /api/brand-brain 500 ..." into its
  // method + path + status for the small detail line. Non-matching
  // errors (TypeError: failed to fetch) fall through to a generic
  // "connection blip" hint with no detail line.
  const apiMatch = error.message.match(/^(GET|POST|PUT|DELETE|PATCH)\s+(\/api\/\S+)\s+(\d{3})/);

  return (
    <main className="relative flex min-h-screen flex-col overflow-hidden bg-[var(--cream)] text-[var(--ink)]">
      <BrandSparkle
        size={320}
        color="var(--ink)"
        style={{
          position: 'absolute',
          top: -50,
          right: -50,
          opacity: 0.08,
          pointerEvents: 'none',
        }}
      />

      <header className="relative flex items-center justify-between px-8 py-5">
        <ErrorWordmark />
        <ErrorEyebrow tone="quiet">
          {'↘'} <Trans>connection · interrupted</Trans>
        </ErrorEyebrow>
      </header>

      <div className="relative flex flex-1 items-center justify-center px-8 pb-10">
        <div className="flex w-full max-w-[520px] flex-col items-start gap-5">
          <Mag size={120} mood="casting" />

          <div>
            <ErrorEyebrow tone="quiet">
              {'↘'} <Trans>couldn't reach mag</Trans>
            </ErrorEyebrow>
            <h1 className="mt-2.5 font-display text-[48px] font-black leading-[1.0] tracking-[-0.04em]">
              <Trans>the line dropped.</Trans>
            </h1>
          </div>

          <p className="max-w-[460px] font-serif text-[19px] leading-[1.4] text-[var(--ink)]/80">
            <Trans>
              “mag's server didn't answer this request. probably a network blip — your campaigns are
              still running. try again in a beat.”
            </Trans>
          </p>

          <div className="flex flex-wrap gap-2.5">
            <button type="button" onClick={handleTryAgain} className="btn">
              <Trans>try again</Trans>
              <span className="btn-arrow">→</span>
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="btn btn-ghost"
            >
              <Trans>hard refresh</Trans>
            </button>
          </div>

          {apiMatch ? (
            <div className="mt-2 w-full rounded-xl border border-[var(--line)] bg-white/70 p-3.5">
              <ErrorEyebrow tone="quiet">
                {'↘'} <Trans>request</Trans>
              </ErrorEyebrow>
              <div className="mt-1.5 font-mono text-[11px] leading-[1.55] text-[var(--ink)]/75">
                <span className="font-bold text-[var(--ink)]">{apiMatch[1]}</span>{' '}
                <span className="text-[var(--ink)]/85">{apiMatch[2]}</span>{' '}
                <span className="text-[var(--pulse)]">→ {apiMatch[3]}</span>
              </div>
            </div>
          ) : (
            <p className="font-mono text-[11px] leading-[1.5] text-[var(--ink)]/55">
              {'↘'} <Trans>if this keeps happening, your wifi or the api is having a moment.</Trans>
            </p>
          )}
        </div>
      </div>
    </main>
  );
}

// ─── 04 · NOT FOUND ────────────────────────────────────────────────────────
// Yellow full-bleed, big 404, Mag confused. Used for unknown routes.

export function NotFoundError() {
  return (
    <main className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[var(--spell)] px-8 py-10 text-[var(--ink)]">
      <BrandSparkle
        size={320}
        color="var(--ink)"
        style={{
          position: 'absolute',
          bottom: -60,
          right: -60,
          opacity: 0.12,
          pointerEvents: 'none',
        }}
      />

      <div className="absolute left-8 top-7">
        <ErrorWordmark />
      </div>

      <div className="relative flex w-full max-w-[560px] flex-col items-center text-center">
        <Mag size={120} mood="confused" />
        <div className="mt-2.5 font-display text-[140px] font-black leading-none tracking-[-0.06em]">
          404
        </div>
        <div className="mt-1 font-serif text-[28px] leading-tight">
          <Trans>“i looked. it's not here.”</Trans>
        </div>
        <p className="mx-auto mt-3.5 max-w-[420px] text-[14.5px] leading-[1.6] text-[var(--ink)]/78">
          <Trans>
            The page you're looking for doesn't exist, was deleted, or never did. Probably a typo
            somewhere.
          </Trans>
        </p>
        <div className="mt-5 flex justify-center">
          {/* `/` is a redirect to `/home`, which itself redirects to /login or
              /brief as the guard sees fit — one button is enough. */}
          <Link to="/" className="btn">
            <Trans>back to home</Trans>
            <span className="btn-arrow">→</span>
          </Link>
        </div>
      </div>
    </main>
  );
}
